import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from "../../../../app/communicationApiSlice";
import toast from "../../../../helpers/toast";

const PREF_LABELS = {
  invoice: { label: "Invoices", desc: "Payment requests and fee reminders" },
  report: { label: "Reports", desc: "Academic report card notifications" },
  timeline_post: { label: "Timeline posts", desc: "New updates from your school" },
  timeline_comment: { label: "Comments", desc: "Replies on posts you interacted with" },
  timeline_reaction: { label: "Reactions", desc: "Reactions on posts you created" },
  chat_message: { label: "Messages", desc: "New private chat messages" },
};

const CHANNEL_LABELS = {
  in_app_enabled: "In-app",
  push_enabled: "Push",
  email_enabled: "Email",
};

export default function NotificationSettings() {
  const { data, isFetching } = useGetNotificationPreferencesQuery();
  const [updatePreferences, { isLoading: isSaving }] =
    useUpdateNotificationPreferencesMutation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (data?.preferences && !draft) {
      setDraft(data.preferences);
    }
  }, [data]);

  const preferences = draft || data?.preferences || [];
  const isDirty =
    JSON.stringify(draft) !== JSON.stringify(data?.preferences || []);

  const updateLocal = (type, key, value) => {
    setDraft((items) =>
      (items || preferences).map((item) =>
        item.type === type ? { ...item, [key]: value } : item
      )
    );
  };

  const handleSave = async () => {
    try {
      await updatePreferences(preferences).unwrap();
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(err?.data?.message || "Could not save preferences");
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Notification settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose how and where you receive each type of notification.
          </Typography>
        </Box>

        {isFetching && <LinearProgress />}

        {preferences.length === 0 && !isFetching && (
          <Alert severity="info">No notification preferences found.</Alert>
        )}

        {preferences.map((pref) => {
          const meta = PREF_LABELS[pref.type] || { label: pref.type, desc: "" };
          return (
            <Paper key={pref.type} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
              >
                <Box>
                  <Typography fontWeight={700}>{meta.label}</Typography>
                  {meta.desc && (
                    <Typography variant="body2" color="text.secondary">
                      {meta.desc}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={3} flexShrink={0}>
                  {Object.entries(CHANNEL_LABELS).map(([key, channelLabel]) => (
                    <FormControlLabel
                      key={key}
                      labelPlacement="bottom"
                      label={
                        <Typography variant="caption" color="text.secondary">
                          {channelLabel}
                        </Typography>
                      }
                      control={
                        <Switch
                          size="small"
                          checked={!!pref[key]}
                          onChange={(e) => updateLocal(pref.type, key, e.target.checked)}
                        />
                      }
                      sx={{ mx: 0 }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>
          );
        })}

        {preferences.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={isSaving || !isDirty}
              onClick={handleSave}
              startIcon={
                isSaving ? <CircularProgress size={14} color="inherit" /> : null
              }
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Save preferences
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
